import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  TableCell,
  withStyles,
} from '@material-ui/core'
import { grey } from '@material-ui/core/colors'
import {
  DataGrid,
  GridAddIcon,
  GridCellParams,
  GridCloseIcon,
  GridColDef,
  GridColumnHeaderParams,
  GridSelectionModel,
} from '@material-ui/data-grid'
import { ArrowDropUp, ArrowDropDown } from '@material-ui/icons'
import { CatalogueEntry } from 'models/catalogues'
import { capitalize } from 'lib/helpers/shared.helpers'
import { useManagementTableStyle } from 'lib/helpers/utilities'
import { getVisualAccountingStep } from 'lib/utils/Accounting/accounting.utils'
import {
  workspaceStatuses,
} from 'lib/utils/Management/management.utils'
import { useUpdateWorkspaceStatus } from 'hooks/queries/management'
import AutocompleteField from 'components/FormComponents/IOComponents/AutocompleteField/AutoCompleteField'
import {
  IWorkspaceItem,
  useManagementWorkspacesContext,
} from '../ManagementWorkspacesContextProvider/ManagementWorkspacesContextProvider'
import WorkspaceDetails from '../ManagementWorkspacesTableDetails/ManagementWorkspacesTableDetails'
import './styles.scss'
import WorkspaceTasksModal from './WorkspaceTasksModal'
import { Button } from '@material-ui/core'
import { useAppSelector } from 'store/hooks'
import {selectAccountingActiveDateRange} from 'store/Accounting/accounting.selectors'
import useGetAccountingTasks from 'hooks/Accounting/useGetAccountingTasks'
import useUpdateAccountingTask from 'hooks/Accounting/useUpdateAccountingTask'

export const CUSTOM_ROW_HEIGHT = 300

export const GrayCheckbox = withStyles({
  root: {},
  checked: {
    color: grey[800],
  },
  indeterminate: {
    color: grey[800],
  },
})((props: any) => <Checkbox color="default" {...props} />)

interface IExpandButtonProps {
  params: GridCellParams
}

const ExpandButton = ({ params }: IExpandButtonProps) => {
  const [expanded, setExpanded] = useState(false)

  const formViewportSize = (height: number) => {
    const { state } = params.api
    const { containerSizes, viewportSizes } = state
    const { dataContainerSizes, windowSizes, totalSizes, renderingZone } =
        containerSizes

    const updateHeight = (obj: any) => ({ ...obj, height })

    return {
      ...state,
      containerSizes: {
        ...containerSizes,
        dataContainerSizes: updateHeight(dataContainerSizes),
        windowSizes: updateHeight(windowSizes),
        totalSizes: updateHeight(totalSizes),
        renderingZone: updateHeight(renderingZone),
      },
      viewportSizes: updateHeight(viewportSizes),
    }
  }

  const ELEMENT_ID = `workspace-details-${params.id}`

  const handleExpand = (params: GridCellParams) => (event: any) => {
    const rowElement = params.api.getRowElement(params.id) as HTMLDivElement
    const viewportHeight = params.api.state.viewportSizes.height
    if (!expanded) {
      const newRow = document.createElement('div')
      newRow.id = ELEMENT_ID
      rowElement.insertAdjacentElement('afterend', newRow)
      params.api.setState(formViewportSize(viewportHeight + CUSTOM_ROW_HEIGHT))
      setExpanded(true)
    } else {
      const nextRow = rowElement.nextElementSibling as HTMLDivElement
      nextRow.remove()
      setExpanded(false)
      params.api.setState(formViewportSize(viewportHeight - CUSTOM_ROW_HEIGHT))
    }

    params.api.updateViewport()
  }

  return (
      <>
        <IconButton onClick={handleExpand(params)}>
          {expanded ? <ArrowDropUp /> : <ArrowDropDown />}
        </IconButton>
        {expanded &&
            createPortal(
                <WorkspaceDetails workspace={params.row as IWorkspaceItem} />,
                document.getElementById(ELEMENT_ID) as HTMLDivElement
            )}
      </>
  )
}

interface ISelectButtonProps {
  params: GridCellParams
}

const SelectButton = ({ params }: ISelectButtonProps) => {
  const [selected, setSelected] = useState(false)

  const isCellSelected = (params: GridCellParams) => {
    const selectedRows = params.api.getSelectedRows()
    return selectedRows.has(params.row.id)
  }

  useEffect(() => {
    if (isCellSelected(params)) {
      setSelected(true)
    } else {
      setSelected(false)
    }
  }, [params])

  const handleSelect = (event: any) => {
    params.api.selectRows([params.id], event.target.checked)
    setSelected(event.target.checked)
  }

  return <GrayCheckbox onClick={handleSelect} checked={selected} />
}

interface ISelectionCellProps {
  params: GridCellParams
}

const SelectionCell = ({ params }: ISelectionCellProps) => {
  return (
      <Box className="options-cell">
        <ExpandButton params={params} />
        <SelectButton params={params} />
      </Box>
  )
}

interface ISelectionHeaderProps {
  params: GridColumnHeaderParams
}

const SelectionHeader = ({ params }: ISelectionHeaderProps) => {
  const {
    allTableRows,
    selectedWorkspaceIds,
    updateManagementWorkspacesState,
  } = useManagementWorkspacesContext()

  const isEveryRowSelected = useMemo(() => {
    const rowIds = params.api.getAllRowIds() as number[]
    const selectedRows = params.api.getSelectedRows()
    return !!rowIds.length ? selectedRows.size === rowIds.length : false
  }, [params])

  const hasRowsSelected = useMemo(() => {
    const selectedRows = params.api.getSelectedRows()
    return selectedRows.size > 0
  }, [params])

  const isIndeterminate = useMemo(() => {
    return hasRowsSelected && !isEveryRowSelected
  }, [hasRowsSelected, isEveryRowSelected])

  const handleSelectAll = (event: any) => {
    if (allTableRows?.length && allTableRows.length > 0) {
      const updatedSelection =
          selectedWorkspaceIds.length === allTableRows.length ? [] : allTableRows

      updateManagementWorkspacesState({
        selectedWorkspaceIds: updatedSelection,
      })
    }
  }

  return (
      <Box className="options-header">
        <GrayCheckbox
            onClick={handleSelectAll}
            indeterminate={isIndeterminate}
            checked={isEveryRowSelected}
        />
      </Box>
  )
}

interface IWorkspacesStatusFieldProps {
  params: GridCellParams
}

const WorkspaceStatusField = ({ params }: IWorkspacesStatusFieldProps) => {
  const [value, setValue] = useState<CatalogueEntry | undefined>(undefined)
  const [isEnable, setIsEnable] = useState<boolean>(false)
  const { value: paramsValue, id: workspaceId } = params
  const { mutate, isLoading } = useUpdateWorkspaceStatus(workspaceId as number)
  const { updateManagementWorkspacesState } = useManagementWorkspacesContext()

  const onChange = (value: any) => {
    if (isEnable) {
      mutate({
        idStatus: value.value,
        name: value.label,
      })
    }

    setIsEnable(true)
  }

  useEffect(() => {
    updateManagementWorkspacesState({ loading: isLoading })
  }, [isLoading, updateManagementWorkspacesState])

  return (
      <Grid item xs={12}>
        <AutocompleteField
            label={''}
            catalogue={workspaceStatuses.map(
                status =>
                    ({ label: status.label, value: status.value }) as CatalogueEntry
            )}
            getOptionLabel={(option: CatalogueEntry) => option.label}
            onSelectValue={(v: CatalogueEntry) => setValue(v)}
            onClear={(v: CatalogueEntry) => setValue(undefined)}
            defaultValue={
              workspaceStatuses.find(
                  status => status.label === paramsValue
              ) as unknown as CatalogueEntry
            }
            value={value}
            fullWidth
            name={`workspaceStatus${params.id}`}
            onChange={onChange}
        />
      </Grid>
  )
}

const ManagementWorkspacesTable = () => {
  const {
    list,
    selectedWorkspaceIds,
    updateManagementWorkspacesState,
    size: pageSize,
    loading,
    managementWorkspacesLoading,
  } = useManagementWorkspacesContext()

  const hydrateWorkspaces = (workspaces: IWorkspaceItem[]) =>
      workspaces.map(workspace => ({ ...workspace, id: workspace.idWorkspace }))

  const [openTasksModal, setOpenTasksModal] = useState(false)
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState<string>('')
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(
      hydrateWorkspaces(list)[0]?.id ?? null
  )

  const activeDateRange = useAppSelector(selectAccountingActiveDateRange)
  const { data: accountingTasks = [] } = useGetAccountingTasks(
      selectedWorkspaceId ?? 0,
      new Date(activeDateRange),
      { enabled: !!selectedWorkspaceId && openTasksModal }
  )

  const toggleAccountingTaskMutation = useUpdateAccountingTask(selectedWorkspaceId ?? 0)

  const handleOpenTasksModal = (workspaceId: number, workspaceName: string) => {
    setSelectedWorkspaceId(workspaceId)
    setSelectedWorkspaceName(workspaceName)
    setOpenTasksModal(true)
  }

  const toggleAccountingTask = (taskId: number, checked: boolean) => {
    const taskToUpdate = accountingTasks.find(t => t.idTask === taskId)
    if (!taskToUpdate) return

    toggleAccountingTaskMutation.mutate({ ...taskToUpdate, checked })
  }

  const columns: GridColDef[] = [
    {
      field: 'selection',
      sortable: false,
      disableColumnMenu: true,
      width: 100,
      renderHeader: params => <SelectionHeader params={params} />,
      renderCell: params => <SelectionCell params={params} />,
      disableReorder: true,
    },
    {
      field: 'id',
      headerName: 'ID',
    },
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
    },
    {
      field: 'rfc',
      headerName: 'RFC',
      flex: 1,
    },
    {
      field: 'team',
      headerName: 'Equipo',
      flex: 1,
    },
    {
      field: 'emailAccountant',
      headerName: 'Contador',
      flex: 1,
      renderCell: ({ row }) => (
          <TableCell className="accountant-cell">
            <Box className="email">{row.emailAccountant}</Box>
          </TableCell>
      ),
    },
    {
      field: 'monthlyClosure',
      headerName: 'Cierre mensual',
      flex: 1,
      renderCell: ({ row }) =>
          getVisualAccountingStep(row.monthlyClosure).value,
    },
    {
      field: 'complianceOpinion',
      headerName: 'Opinión',
      flex: 1,
      renderCell: params => {
        if (!params.value) return 'Sin opinión'
        return (
            <TableCell className="rating-cell">
              {params.value === 'positiva' ? <GridAddIcon /> : <GridCloseIcon />}
              <Box>{capitalize(params.value as string)}</Box>
            </TableCell>
        )
      },
    },
    {
      field: 'coefficient',
      headerName: 'Coeficiente',
      flex: 1,
    },
    {
      field: 'workspaceStatus',
      headerName: 'Estatus de espacio de trabajo',
      flex: 1,
      renderCell: params => (
          <TableCell className="workspace-status-cell">
            <WorkspaceStatusField params={params} key={params.id} />
          </TableCell>
      ),
    },
    {
      field: 'tareas',
      headerName: 'Tareas',
      headerAlign: 'center',
      align: 'center',
      width: 150,
      renderCell: (params) => (
          <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
          >
            <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={() =>
                    handleOpenTasksModal(params.row.id, params.row.name)
                }
            >
              Ver Tareas
            </Button>
          </Box>
      ),
    }
  ]

  const {
    page,
    totalResults,
    fetchManagementWorkspaces,
  } = useManagementWorkspacesContext()

  return (
      <div className="workspaces-table-component">
        <DataGrid
            className={useManagementTableStyle().root}
            columns={columns}
            rows={hydrateWorkspaces(list)}
            autoHeight
            page={page - 1}
            pageSize={pageSize}
            rowCount={totalResults}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => fetchManagementWorkspaces(newPage + 1)}
            disableSelectionOnClick
            disableColumnMenu
            selectionModel={selectedWorkspaceIds.filter(id =>
                hydrateWorkspaces(list).some(w => w.id === id)
            )}
            onSelectionModelChange={(selection: GridSelectionModel) => {
              const newId = selection[0] as number
              if (newId) setSelectedWorkspaceId(newId)

              updateManagementWorkspacesState(prevState => ({
                ...prevState,
                selectedWorkspaceIds: selection.map(id => id as number),
              }))
            }}
            loading={managementWorkspacesLoading || loading}
        />


        {openTasksModal && (
            <WorkspaceTasksModal
                workspaceName={selectedWorkspaceName}
                tasks={accountingTasks ?? []}
                open={openTasksModal}
                onClose={() => setOpenTasksModal(false)}
                onToggleTask={toggleAccountingTask}
            />
        )}
      </div>
  )
}

export default ManagementWorkspacesTable